import { Component } from "@theme/component";

const REVIEWS_URL = "https://api.reviews.io/product/review";

/**
 * Review count requests in flight or completed, so several elements on a page share one request.
 *
 * @type {Map<string, Promise<number>>}
 */
const countRequests = new Map();

/**
 * @param {string} storeKey - The public REVIEWS.io store key.
 * @param {string} sku - Semicolon separated product identifiers, as used by the rating snippet.
 * @returns {Promise<number>}
 */
function fetchReviewCount(storeKey, sku) {
  const cacheKey = `${storeKey}:${sku}`;
  const existingRequest = countRequests.get(cacheKey);
  if (existingRequest) return existingRequest;

  const params = new URLSearchParams({ store: storeKey, sku, per_page: "1" });

  const request = fetch(`${REVIEWS_URL}?${params}`)
    .then((response) => {
      if (!response.ok) throw new Error(`REVIEWS.io request failed: ${response.status}`);
      return response.json();
    })
    .then((data) => Number(data?.stats?.count));

  countRequests.set(cacheKey, request);
  return request;
}

/**
 * Hides the section it sits in when the product has no reviews.
 * The section stays visible if the count can't be loaded, and in the theme editor so it can still be edited.
 *
 * @extends {Component}
 */
class ProductReviewsVisibilityComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#updateVisibility();
  }

  async #updateVisibility() {
    const storeKey = this.getAttribute("store-key");
    const sku = this.getAttribute("sku");
    const section = this.closest(".shopify-section");
    if (!storeKey || !sku || !section || window.Shopify?.designMode) return;

    try {
      const count = await fetchReviewCount(storeKey, sku);
      section.hidden = count === 0;
    } catch {
      // Keep the section visible.
    }
  }
}

if (!customElements.get("product-reviews-visibility")) {
  customElements.define("product-reviews-visibility", ProductReviewsVisibilityComponent);
}
